export const CAREER_PASSPORT_DEFINITIONS = [
  {
    id: 'digital',
    title: 'Digital Passport',
    tone: 'digital',
    nqfLevels: 'NQF Levels 2–7',
    description: 'Competencies for participating, working and building in a digital economy.',
    coverage: 'Basic data literacy, artificial intelligence, cybersecurity, cloud computing and software development.',
    courseKeywords: [
      'digital', 'data literacy', 'artificial intelligence', 'cybersecurity', 'cyber security',
      'cloud computing', 'software', 'web development', 'programming', 'javascript', 'react',
      'information technology', 'it support', 'network', 'ui/ux', 'user experience'
    ]
  },
  {
    id: 'green',
    title: 'Green Passport',
    tone: 'green',
    nqfLevels: 'NQF Levels 2–6',
    description: 'Skills for sustainable industries, climate resilience and a greener economy.',
    coverage: 'Renewable energy technologies, green construction, environmental compliance and sustainability management.',
    courseKeywords: [
      'renewable', 'solar', 'wind energy', 'environmental', 'sustainability', 'sustainable',
      'green construction', 'waste management', 'water management', 'climate', 'energy efficiency'
    ]
  },
  {
    id: 'industrial-occupational',
    title: 'Industrial & Occupational Passport',
    tone: 'industrial',
    nqfLevels: 'NQF Levels 2–6',
    description: 'Practical and technical pathways into trades, engineering and production.',
    coverage: 'Trades, artisanship, engineering and advanced manufacturing, aligned to QCTO pathways.',
    courseKeywords: [
      'trade', 'artisan', 'engineering', 'manufacturing', 'production', 'welding', 'welder', 'electrician',
      'electrical', 'plumbing', 'plumber', 'carpenter', 'carpentry', 'bricklayer', 'boilermaker',
      'fitter', 'millwright', 'mechanic', 'automotive', 'construction', 'mechanical', 'maintenance'
    ]
  },
  {
    id: 'entrepreneurial',
    title: 'Entrepreneurial Passport',
    tone: 'entrepreneurial',
    nqfLevels: 'NQF Levels 2–8',
    description: 'Capabilities for starting, formalising and growing sustainable enterprises.',
    coverage: 'SMME development, business registration, financial costing, procurement navigation and enterprise development support.',
    courseKeywords: [
      'business', 'entrepreneur', 'enterprise', 'smme', 'administration', 'accounting', 'finance',
      'financial', 'procurement', 'marketing', 'project management', 'business management',
      'retail', 'costing', 'management'
    ]
  },
  {
    id: 'social-civic',
    title: 'Social & Civic Passport',
    tone: 'social-civic',
    nqfLevels: 'NQF Levels 1–5',
    description: 'Foundational values and capabilities for community participation and public contribution.',
    coverage: 'Civic participation, community service, ethics, conflict resolution and developmental workforce behaviours.',
    courseKeywords: [
      'civic', 'community', 'public service', 'social development', 'ethics', 'conflict resolution',
      'governance', 'community health', 'healthcare', 'care work', 'social care', 'youth development'
    ]
  },
  {
    id: 'dynamic-interpersonal',
    title: 'Dynamic Interpersonal Skills Passport',
    tone: 'interpersonal',
    nqfLevels: 'All NQF levels',
    description: 'Transferable capabilities for navigating complex and rapidly changing work environments.',
    coverage: 'Critical thinking, communication, teamwork, leadership, resilience and adaptability.',
    courseKeywords: [
      'communication', 'teamwork', 'leadership', 'critical thinking', 'resilience', 'adaptability',
      'customer service', 'facilitation', 'human resources', 'workplace readiness', 'hospitality',
      'project management', 'management'
    ]
  }
];

const getNqfLevel = (value) => {
  const match = String(value ?? '').match(/\d+/);
  return match ? Number(match[0]) : null;
};

export const PASSPORT_BELTS = ['white', 'yellow', 'orange', 'green', 'blue', 'purple', 'brown', 'black'];
export const PASSPORT_STANDARDS = ['first', 'second', 'third'];
export const PASSPORT_PROGRESSION_NOTE = 'White to Black within each standard. An assessed Black Belt completes that standard only; NQF level and course progress are separate.';

// These are immutable, server-verified achievement snapshots, never live course targets.
export const getPassportProgressState = (passportId, enrollments = []) => {
  const awards = enrollments.flatMap(enrollment => enrollment?.metadata?.passport_awards || [])
    .filter(award => award.passport_id === passportId && award.source === 'lms_assessed_completion' &&
      PASSPORT_STANDARDS.includes(award.standard) && PASSPORT_BELTS.includes(award.belt));
  const standards = PASSPORT_STANDARDS.map(standard => {
    const earned = awards.filter(award => award.standard === standard);
    const highest = earned.reduce((best, award) => !best || PASSPORT_BELTS.indexOf(award.belt) > PASSPORT_BELTS.indexOf(best.belt) ? award : best, null);
    const belt = highest?.belt || '';
    const standardComplete = earned.some(award => award.belt === 'black' && award.standard_complete === true);
    return { standard, belt, awards: earned, standardComplete,
      earnedLabel: belt ? `${standard.charAt(0).toUpperCase()}${standard.slice(1)} Standard · ${belt.charAt(0).toUpperCase()}${belt.slice(1)} Belt` : '' };
  });
  const highest = [...standards].reverse().find(standard => standard.belt);
  return { awards, standards, standard: highest?.standard || '', belt: highest?.belt || '',
    earnedLabel: highest?.earnedLabel || '', standardComplete: highest?.standardComplete || false,
    hasAward: awards.length > 0, allStandardsComplete: standards.every(standard => standard.standardComplete) };
};

export const getCoursePassportProgressions = (course) => {
  const nqfLevel = getNqfLevel(course?.nqf_exit_level);

  return getCoursePassportLinks(course).map(passport => {
    const belt = passport.belt
      ? `${passport.belt.charAt(0).toUpperCase()}${passport.belt.slice(1)}`
      : '';
    const standard = passport.standard
      ? ({ first: 'First Standard', second: 'Second Standard', third: 'Third Standard' }[passport.standard] || passport.standard)
      : '';
    const hasConfiguredProgression = Boolean(belt || standard);

    return {
      ...passport,
      belt,
      standard,
      hasConfiguredProgression,
      progressionLabel: belt && standard ? `${belt} Belt · ${standard}` : '',
      targetLabel: belt && standard
        ? [belt && `${belt} Belt`, standard].filter(Boolean).join(' · ')
        : passport.alignment_source === 'inferred_needs_review'
          ? 'Suggested pathway · belt and standard need confirmation'
          : passport.alignment_source === 'staff_confirmed'
            ? `Passport linked · ${belt ? 'standard' : standard ? 'belt' : 'belt and standard'} not assigned`
            : 'Course target not mapped',
      standardLabel: passport.standard_label || (belt && standard
        ? 'Course target mapped'
        : `Passport linked · ${belt ? 'standard' : standard ? 'belt' : 'belt and standard'} not assigned`),
      nqfLevel,
      progressionNote: passport.alignment_source === 'inferred_needs_review'
        ? 'Suggested from course wording; staff confirmation is still required.'
        : passport.alignment_note || (hasConfiguredProgression
          ? `This is the course pathway target, not a learner award. ${PASSPORT_PROGRESSION_NOTE}`
          : 'Belts and standards are not yet mapped for this course.')
    };
  });
};

const getCourseSearchText = (course) => [
  course?.programme_name,
  course?.description,
  course?.course_type,
  course?.trade_specialisation,
  course?.category?.name,
  course?.category
].filter(Boolean).join(' ').toLowerCase();

export const getCoursePassportLinks = (course) => {
  if (Array.isArray(course?.passport_links)) {
    return course.passport_links.map(link => {
      const definition = CAREER_PASSPORT_DEFINITIONS.find(item => item.id === link.id);
      return {
        ...definition,
        ...link,
        title: link.title || definition?.title || link.id,
        tone: link.tone || definition?.tone || 'digital',
        coverage: link.coverage || definition?.coverage || ''
      };
    }).filter(link => link.id);
  }

  if (course?.passport_alignment_reviewed) return [];

  const searchableText = getCourseSearchText(course);

  return CAREER_PASSPORT_DEFINITIONS.filter(passport => (
    passport.courseKeywords.some(keyword => searchableText.includes(keyword))
  )).map(passport => ({ ...passport, alignment_source: 'inferred_needs_review' }));
};
